import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, User } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { apiGet, apiPost } from '../../../utils/api';
import PricingCards from '../../pricing/PricingCards';
import EmbeddedCheckoutModal from '../../pricing/EmbeddedCheckoutModal';

const UpgradePage = () => {
  const [interval, setInterval] = useState('monthly');
  const [billingType, setBillingType] = useState('individual');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  const hasClient = !!currentUser?.clientMembership;
  const isMultiClient = currentUser?.isMultiClient;

  useEffect(() => {
    if (location.state?.interval) setInterval(location.state.interval);
  }, [location.state]);

  const { data: plans = [], isLoading: plansLoading } = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: async () => {
      const res = await apiGet('/billing/plans');
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data) ? data : (data.plans || []);
    },
  });

  const { data: subsData, isLoading: subsLoading } = useQuery({
    queryKey: ['billing', 'subscriptions'],
    queryFn: async () => {
      const res = await apiGet('/billing/subscriptions');
      return res.ok ? res.json() : { subscriptions: [], exempt: false };
    },
  });

  const subscriptions = subsData?.subscriptions || [];
  const exempt = subsData?.exempt || false;

  const checkoutMutation = useMutation({
    mutationFn: async (plan) => {
      const payload = {
        plan_id: plan.id,
        interval,
        billing_type: hasClient ? billingType : 'individual',
      };
      if (billingType === 'org' && selectedClientId) {
        payload.client_id = selectedClientId;
      }
      if (discountCode.trim()) payload.discount_code = discountCode.trim();
      const res = await apiPost('/billing/checkout', payload);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to create checkout session');
      }
      return res.json();
    },
    onSuccess: (data) => {
      // Embedded Checkout: mount the modal with the session's client_secret.
      // Stripe handles payment inside the iframe; onComplete (in the modal)
      // closes it and triggers a subscription refetch.
      setCheckoutClientSecret(data.client_secret);
    },
  });

  // A subscription is "owned" by the current user when they pay or manage it:
  //   - Individual subs (org_subscription === false) belong to the user.
  //   - Org subs are owned only when the current user is a client_manager
  //     of that specific client_id.
  // Org subs the user is just a member of give "access" but not ownership —
  // they should see them as read-only context, not in plan selection CTAs.
  const isClientManagerOf = (clientId) =>
    (currentUser?.clientMemberships || []).some(
      cm => cm.client_id === clientId && cm.client_role === 'client_manager'
    );

  // Plan keys the user OWNS (can manage seats, cancel, upgrade).
  const ownedPlanKeys = subscriptions
    .filter(s => s.active_access)
    .filter(s => !s.org_subscription || isClientManagerOf(s.client_id))
    .map(s => s.plan_key);

  // Whether the user has *any* feature access — owned or via org membership.
  // Used to reframe checkout copy ("you already have access; subscribing
  // would give you a plan you own with seats to share").
  const accessProvidingSubs = subscriptions.filter(s => s.active_access);
  const userHasAnyAccess = accessProvidingSubs.length > 0;
  const accessProviderName =
    accessProvidingSubs.find(s => s.org_subscription && !ownedPlanKeys.includes(s.plan_key))
      ?.client_name || null;

  // Sort plans based on billing type: personal = solo (1-seat) first, org = multi-seat first
  const sortedPlans = useMemo(() => {
    if (!plans.length) return plans;
    return [...plans].sort((a, b) => {
      const aIsSolo = (a.included_seats || 0) <= 1;
      const bIsSolo = (b.included_seats || 0) <= 1;
      if (billingType === 'individual') {
        if (aIsSolo && !bIsSolo) return -1;
        if (!aIsSolo && bIsSolo) return 1;
      } else {
        if (!aIsSolo && bIsSolo) return -1;
        if (aIsSolo && !bIsSolo) return 1;
      }
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
  }, [plans, billingType]);

  const loading = plansLoading || subsLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-6 max-w-screen-2xl mx-auto">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-dm-serif font-bold dark:text-white mb-2">
            {exempt ? 'Your Plan' : 'Upgrade Your Plan'}
          </h1>
          {exempt ? (
            <p className="text-gray-500 dark:text-gray-400 font-rasa">
              Your role grants you full access to all platform features.
            </p>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 font-unna">
              Choose the compartments you need, or save with All Access.
            </p>
          )}
        </div>

        {checkoutMutation.isError && (
          <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 mb-6 text-center">
            {checkoutMutation.error?.message}
          </div>
        )}

        {checkoutMutation.isPending && (
          <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 mb-6 text-center font-rasa">
            Redirecting to checkout...
          </div>
        )}

        {/* Current subscriptions summary */}
        {subscriptions.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
            <h3 className="text-sm font-rasa font-semibold text-gray-500 dark:text-gray-400 mb-2">Active Subscriptions</h3>
            <div className="flex flex-wrap gap-2">
              {subscriptions.filter(s => s.active_access).map(sub => (
                <span key={sub.id} className="inline-flex items-center gap-1 px-3 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-full text-sm font-rasa">
                  {sub.plan_name}
                  <span className="text-xs text-green-500">({sub.source === 'stripe' ? sub.billing_interval : sub.source.replace('_', ' ')})</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {!exempt && (
          <>
            {/* Billing type selector (for users with a client) */}
            {hasClient && (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6">
                <p className="text-sm font-rasa font-semibold text-gray-500 dark:text-gray-400 mb-3">Who is this subscription for?</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setBillingType('individual')}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 text-left transition-colors ${
                      billingType === 'individual'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <User size={20} className={billingType === 'individual' ? 'text-blue-500' : 'text-gray-400'} />
                    <div>
                      <p className="text-sm font-rasa font-medium dark:text-white">Personal</p>
                      <p className="text-xs font-rasa text-gray-500 dark:text-gray-400">Billed to you — share included seats with anyone via email invite</p>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      setBillingType('org');
                      if (!selectedClientId && currentUser.clientMemberships?.length > 0) {
                        setSelectedClientId(String(currentUser.clientMemberships[0].client_id));
                      }
                    }}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 text-left transition-colors ${
                      billingType === 'org'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <Users size={20} className={billingType === 'org' ? 'text-blue-500' : 'text-gray-400'} />
                    <div>
                      <p className="text-sm font-rasa font-medium dark:text-white">Organization</p>
                      <p className="text-xs font-rasa text-gray-500 dark:text-gray-400">Billed to your company — assign seats to team members</p>
                    </div>
                  </button>
                </div>

                {/* Client picker — shown when org is selected and user has multiple clients */}
                {billingType === 'org' && currentUser.clientMemberships?.length > 1 && (
                  <div className="mt-3 col-span-2">
                    <label className="block text-sm font-rasa text-gray-500 dark:text-gray-400 mb-1">Which organization?</label>
                    <select
                      value={selectedClientId}
                      onChange={(e) => setSelectedClientId(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border text-sm font-rasa dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      {currentUser.clientMemberships.map((cm) => (
                        <option key={cm.client_id} value={cm.client_id}>{cm.client_company_name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Interval Toggle */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <button
                onClick={() => setInterval('monthly')}
                className={`px-4 py-2 rounded-lg font-rasa text-sm font-medium transition-colors ${
                  interval === 'monthly'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setInterval('yearly')}
                className={`px-4 py-2 rounded-lg font-rasa text-sm font-medium transition-colors ${
                  interval === 'yearly'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                }`}
              >
                Yearly
                <span className="ml-1.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-1.5 py-0.5 rounded">Save</span>
              </button>
            </div>

            {/* Discount code */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <div className="relative">
                <input
                  type="text"
                  value={discountCode}
                  onChange={e => setDiscountCode(e.target.value.toUpperCase())}
                  placeholder="Discount code"
                  className="px-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm font-mono w-48 text-center"
                />
                {discountCode && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-green-500 font-rasa">Applied at checkout</span>
                )}
              </div>
            </div>

            <PricingCards
              plans={sortedPlans}
              interval={interval}
              onSelect={(plan) => checkoutMutation.mutate(plan)}
              ownedPlanKeys={ownedPlanKeys}
              userHasAnyAccess={userHasAnyAccess}
              accessProviderName={accessProviderName}
              onManageSeats={() =>
                navigate('/dashboard/settings', { state: { initialTab: 'billing' } })
              }
            />
          </>
        )}
      </div>
      <EmbeddedCheckoutModal
        clientSecret={checkoutClientSecret}
        onClose={() => setCheckoutClientSecret(null)}
        onComplete={() => {
          // Stripe finished processing inside the iframe. Close the modal
          // and refetch subscriptions so the user sees the new plan
          // immediately when they land back on /dashboard/billing.
          setCheckoutClientSecret(null);
          queryClient.invalidateQueries({ queryKey: ['billing', 'subscriptions'] });
        }}
      />
    </div>
  );
};

export default UpgradePage;
